// ============================================================================
// CHANGO V5.0 — 2FA Login Challenge Page
// ============================================================================
// Reached from Login when the backend responds with
//   { two_factor_required: true, challenge: '<opaque 64-char token>' }
// Expects a 6-digit TOTP code (or optionally a backup code; the backend
// endpoints are symmetric). On success, useAuth().loginTwoFa hydrates the
// session and we bounce to the original destination.
// ============================================================================

import React, { useEffect, useState } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';

import { useAuth } from '../../hooks/useAuth.js';
import { api, HttpError } from '../../api/client.js';

export default function TwoFALogin() {
  const location = useLocation();
  const navigate = useNavigate();
  const { loginTwoFa } = useAuth();

  const challenge = location.state?.challenge;
  const from = location.state?.from || '/app/dashboard';

  const [mode, setMode] = useState('totp'); // 'totp' | 'backup'
  const [code, setCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState(null);

  // Guard: if the user hits /2fa directly without a challenge, send them back.
  if (!challenge) {
    return <Navigate to="/login" replace />;
  }

  const submit = async (e) => {
    e?.preventDefault();
    setServerError(null);
    setSubmitting(true);
    try {
      if (mode === 'totp') {
        await loginTwoFa(challenge, code);
      } else {
        await api.post('/api/auth/2fa/backup', { challenge, backup_code: code });
        // Backup flow doesn't drive through AuthContext.loginTwoFa; ask the
        // context to re-hydrate explicitly via a fresh /me call.
        window.location.assign(from);
        return;
      }
      navigate(from, { replace: true });
    } catch (err) {
      if (err instanceof HttpError) {
        if (err.code === 'OTP_INVALID') setServerError('验证码错误');
        else if (err.code === 'CHALLENGE_EXPIRED') setServerError('验证已超时，请重新登录');
        else setServerError(err.message || '验证失败');
      } else {
        setServerError('网络错误，请重试');
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Auto-submit for TOTP once six digits are entered.
  useEffect(() => {
    if (mode === 'totp' && /^\d{6}$/.test(code) && !submitting) {
      submit();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code, mode]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 px-4">
      <div className="card w-full max-w-md p-8">
        <h1 className="text-xl font-semibold text-slate-800">两步验证</h1>
        <p className="mt-2 text-sm text-slate-600">
          {mode === 'totp'
            ? '请打开您的验证器应用（如 Google Authenticator / 1Password），输入当前的 6 位动态验证码。'
            : '请输入一条未使用过的备用码（格式为 4-4-4 的字母数字组合）。'}
        </p>

        {serverError ? <div role="alert" className="alert-error mt-4">{serverError}</div> : null}

        <form noValidate onSubmit={submit} className="mt-6 space-y-4">
          <div>
            <label className="label" htmlFor="otp">{mode === 'totp' ? '6 位验证码' : '备用码'}</label>
            <input
              id="otp"
              inputMode={mode === 'totp' ? 'numeric' : 'text'}
              autoComplete="one-time-code"
              autoFocus
              maxLength={mode === 'totp' ? 6 : 20}
              className="field text-center font-mono text-lg tracking-widest"
              value={code}
              onChange={(e) => {
                const v = mode === 'totp'
                  ? e.target.value.replace(/\D/g, '').slice(0, 6)
                  : e.target.value.trim();
                setCode(v);
              }}
              disabled={submitting}
            />
          </div>

          {mode === 'backup' ? (
            <button
              type="submit"
              disabled={submitting || code.length < 8}
              className="btn-primary w-full"
            >
              {submitting ? <span className="spinner" aria-hidden="true" /> : null}
              {submitting ? '验证中…' : '验证备用码'}
            </button>
          ) : null}

          <div className="flex items-center justify-between text-sm">
            <button
              type="button"
              className="text-chango-600 hover:underline"
              onClick={() => {
                setMode((m) => (m === 'totp' ? 'backup' : 'totp'));
                setCode('');
                setServerError(null);
              }}
            >
              {mode === 'totp' ? '改用备用码登录' : '改用验证器登录'}
            </button>
            <Link to="/login" className="text-slate-500 hover:text-slate-700">返回登录</Link>
          </div>
        </form>
      </div>
    </div>
  );
}
